import { createTheme } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    backgroundBlue: {
      main: string;
    };
    tertiary: {
      light: string;
      main: string;
      dark: string;
      contrastText: string;
    };
    messageGreen: {
      main: string;
    };
  }
  interface PaletteOptions {
    backgroundBlue?: {
      main?: string;
    };
    tertiary?: {
      light?: string;
      main?: string;
      dark?: string;
      contrastText?: string;
    };
    messageGreen?: {
      main?: string;
    };
  }
}

// Font family definitions with proper fallbacks
const radioCanadaBigFontFamily =
  '"Radio Canada Big", "Radio Canada", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif';

// Font optimization settings that will be applied globally
const fontOptimization = {
  fontFeatureSettings: '"kern" 1, "liga" 1, "calt" 1, "ss01" 1',
  WebkitFontSmoothing: 'antialiased' as const,
  MozOsxFontSmoothing: 'grayscale' as const,
  textRendering: 'optimizeLegibility' as const
};

export const homeownerPortalTheme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#13284c' // Dark blue for titles, buttons, emphasis etc.
    },
    secondary: {
      main: '#388e3c',
      light: '#66bb6a',
      dark: '#2e7d32'
    },
    tertiary: {
      light: '#ffcd38',
      main: '#ffc107', // Yellow/amber - matching main theme
      dark: '#b8860b',
      contrastText: '#000'
    },
    messageGreen: {
      main: '#8bc640' // Green - matching main theme
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff'
    },
    text: {
      primary: '#333333',
      secondary: '#666666'
    },
    backgroundBlue: {
      main: '#e2e9f1'
    }
  },
  typography: {
    fontFamily: radioCanadaBigFontFamily, // Default to Radio Canada Big
    ...fontOptimization,
    h1: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 700,
      fontSize: '3.25rem',
      lineHeight: 1.1,
      ...fontOptimization,
      '@media (max-width:600px)': {
        fontSize: '2.25rem',
        lineHeight: 0.75
      }
    },
    h2: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 600,
      fontSize: '1.5rem',
      lineHeight: 1.3,
      ...fontOptimization,
      '@media (max-width:600px)': {
        fontSize: '1.25rem'
      }
    },
    h3: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 500,
      fontSize: '1.5rem',
      ...fontOptimization,
      '@media (max-width:600px)': {
        fontSize: '1.25rem'
      }
    },
    h4: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 700,
      color: '#13284c',
      marginBottom: '1rem',
      fontSize: '1.5rem',
      ...fontOptimization
    },
    h5: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 700,
      fontSize: '1.25rem',
      ...fontOptimization
    },
    h6: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 700,
      fontSize: '1rem',
      ...fontOptimization
    },
    subtitle1: {
      fontFamily: radioCanadaBigFontFamily,
      fontSize: '1.125rem',
      color: '#666666',
      lineHeight: 1.5,
      fontWeight: 400,
      ...fontOptimization
    },
    subtitle2: {
      fontFamily: radioCanadaBigFontFamily,
      fontSize: '1rem',
      color: '#666666',
      lineHeight: 1.5,
      fontWeight: 500,
      ...fontOptimization
    },
    body1: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 500,
      fontSize: '0.9375rem',
      lineHeight: 1.6,
      ...fontOptimization
    },
    body2: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 400,
      fontSize: '0.875rem',
      lineHeight: 1.5,
      ...fontOptimization
    },
    button: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 600,
      textTransform: 'none',
      ...fontOptimization
    },
    caption: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 400,
      fontSize: '0.75rem',
      ...fontOptimization
    },
    overline: {
      fontFamily: radioCanadaBigFontFamily,
      fontWeight: 400,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.08333em',
      ...fontOptimization
    }
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '0px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          transition: 'all 0.3s ease',
          cursor: 'pointer',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)'
          }
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          textTransform: 'none',
          fontFamily: radioCanadaBigFontFamily,
          fontWeight: 400,
          padding: '0px 32px',
          fontSize: '1.125rem',
          transition: 'all 0.3s ease',
          ...fontOptimization
        },
        contained: {
          backgroundColor: '#13284c',
          color: 'white',
          boxShadow: '0 2px 8px rgba(19, 40, 76, 0.3)',
          '&:hover': {
            backgroundColor: '#0f1f36',
            boxShadow: '0 4px 16px rgba(19, 40, 76, 0.4)',
            transform: 'translateY(-2px)'
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        },
        outlined: {
          backgroundColor: 'white',
          color: '#13284c',
          border: '2px solid #13284c',
          boxShadow: '0 2px 8px rgba(19, 40, 76, 0.1)',
          '&:hover': {
            backgroundColor: '#f8f9fa',
            boxShadow: '0 4px 16px rgba(19, 40, 76, 0.2)',
            transform: 'translateY(-2px)'
          },
          '&:active': {
            transform: 'translateY(0)'
          }
        },
        text: {
          backgroundColor: 'transparent',
          color: '#13284c',
          border: '1px solid #13284c',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(25, 118, 210, 0.4)'
          }
        }
      }
    }
  }
});
